import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { doc, updateDoc } from "firebase/firestore";
import { sendEmailVerification } from "firebase/auth";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import DashboardNav from "../components/DashboardNav";
import {
  Users,
  Link as LinkIcon,
  CreditCard,
  CheckCircle2,
  AlertCircle,
  Loader2,
  MonitorPlay,
  Check,
  ArrowLeft,
  Phone,
  Mail,
  ShieldCheck,
} from "lucide-react";

const PLATFORM_OPTIONS = [
  { id: "youtube", label: "YouTube" },
  { id: "twitch", label: "Twitch" },
  { id: "tiktok", label: "TikTok" },
  { id: "twitter", label: "X / Twitter" },
  { id: "instagram", label: "Instagram" },
  { id: "kick", label: "Kick" },
];

const AUDIENCE_OPTIONS = [
  { value: 100, label: "100 – 999" },
  { value: 1000, label: "1,000 – 4,999" },
  { value: 5000, label: "5,000 – 9,999" },
  { value: 10000, label: "10,000 – 49,999" },
  { value: 50000, label: "50,000 – 99,999" },
  { value: 100000, label: "100,000 – 499,999" },
  { value: 500000, label: "500,000 – 999,999" },
  { value: 1000000, label: "1,000,000+" },
];

function InfluencerProfilePage() {
  const { currentUser, userProfile, refreshProfile } = useAuth();
  const navigate = useNavigate();

  const [audienceSize, setAudienceSize] = useState(0);
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [links, setLinks] = useState({
    youtube: "",
    twitch: "",
    tiktok: "",
    twitter: "",
    instagram: "",
    kick: "",
  });
  const [paymentMethod, setPaymentMethod] = useState("paypal");
  const [paymentEmail, setPaymentEmail] = useState("");
  const [paymentIban, setPaymentIban] = useState("");
  const [paymentName, setPaymentName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [username, setUsername] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [sendingVerification, setSendingVerification] = useState(false);

  useEffect(() => {
    if (userProfile?.influencerProfile) {
      const p = userProfile.influencerProfile;
      setAudienceSize(p.audienceSize || 0);
      setSelectedPlatforms(p.platforms || []);
      setLinks((prev) => ({ ...prev, ...(p.links || {}) }));
      setPaymentMethod(p.paymentMethod || "paypal");
      setPaymentEmail(p.paymentEmail || "");
      setPaymentIban(p.paymentIban || "");
      setPaymentName(p.paymentName || "");
      setPhoneNumber(p.phoneNumber || "");
      setUsername(p.username || "");
    }
  }, [userProfile]);

  function togglePlatform(platformId) {
    setSelectedPlatforms((prev) =>
      prev.includes(platformId)
        ? prev.filter((p) => p !== platformId)
        : [...prev, platformId]
    );
  }

  function handleLinkChange(platformId, value) {
    setLinks((prev) => ({ ...prev, [platformId]: value }));
  }

  async function handleSave(e) {
    e.preventDefault();
    setError("");
    setSuccess(false);

    if (!username.trim()) {
      setError("Please enter a username.");
      return;
    }
    if (!audienceSize) {
      setError("Please select your audience size.");
      return;
    }
    if (selectedPlatforms.length === 0) {
      setError("Please select at least one platform.");
      return;
    }
    const hasAtLeastOneLink = selectedPlatforms.some(
      (p) => links[p]?.trim()
    );
    if (!hasAtLeastOneLink) {
      setError("Please add a link for at least one of your selected platforms.");
      return;
    }
    if (paymentMethod === "iban") {
      if (!paymentIban.trim()) {
        setError("Please enter your IBAN number.");
        return;
      }
      if (!paymentName.trim()) {
        setError("Please enter the account holder name.");
        return;
      }
    } else {
      if (!paymentEmail.trim()) {
        setError("Please enter your payment email.");
        return;
      }
    }

    setLoading(true);
    try {
      const profileData = {
        username: username.trim(),
        audienceSize: parseInt(audienceSize),
        platforms: selectedPlatforms,
        links: Object.fromEntries(
          selectedPlatforms
            .filter((p) => links[p]?.trim())
            .map((p) => [p, links[p].trim()])
        ),
        paymentMethod,
        paymentEmail: paymentMethod !== "iban" ? paymentEmail.trim() : "",
        paymentIban: paymentMethod === "iban" ? paymentIban.trim() : "",
        paymentName: paymentMethod === "iban" ? paymentName.trim() : "",
        phoneNumber: phoneNumber.trim(),
        profileCompleted: true,
        updatedAt: new Date().toISOString(),
      };

      const updateData = { influencerProfile: profileData };

      // Generate utmId from username if not already set
      if (!userProfile?.utmId) {
        const base = username.trim()
          .toLowerCase()
          .replace(/[^a-z0-9]/g, "")
          .slice(0, 20);
        const digits = Math.floor(100000 + Math.random() * 900000);
        updateData.utmId = `${base}${digits}`;
      }

      await updateDoc(doc(db, "users", currentUser.uid), updateData);

      if (refreshProfile) await refreshProfile();
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error("Failed to save profile:", err);
      setError("Failed to save profile. Please try again.");
    }
    setLoading(false);
  }

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <DashboardNav />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <button
          onClick={() => navigate("/dashboard")}
          className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-6 text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </button>

        <div className="flex items-start justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold mb-1">Influencer Profile</h1>
            <p className="text-gray-400">
              Complete your profile to start applying for campaigns.
            </p>
            {userProfile?.isApprovedByAdmin ? (
              <span className="inline-flex items-center gap-1.5 mt-2 text-xs font-medium text-green-400 bg-green-500/10 border border-green-500/20 px-2.5 py-1 rounded-full">
                <ShieldCheck className="w-3.5 h-3.5" />
                Verified
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 mt-2 text-xs font-medium text-yellow-400 bg-yellow-500/10 border border-yellow-500/20 px-2.5 py-1 rounded-full">
                <AlertCircle className="w-3.5 h-3.5" />
                Not Verified
              </span>
            )}
            {userProfile?.utmId && (
              <p className="text-sm text-gray-500 mt-2">
                Your UTM ID:{" "}
                <code className="bg-gray-800 px-2 py-0.5 rounded text-brand-400">
                  {userProfile.utmId}
                </code>
              </p>
            )}
          </div>
          <Link
            to="/links/edit"
            className="flex items-center gap-2 bg-brand-600 hover:bg-brand-500 text-white px-5 py-2.5 rounded-xl transition-all font-semibold text-sm shadow-lg shadow-brand-600/25 flex-shrink-0"
          >
            <LinkIcon className="w-4 h-4" />
            gamesin.bio Page
          </Link>
        </div>

        <form onSubmit={handleSave} className="space-y-8">
          {/* Email Verification */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
              <Mail className="w-4 h-4 text-gray-500" />
              Email Verification
            </label>
            {currentUser?.emailVerified ? (
              <div className="flex items-center gap-2 bg-green-500/5 border border-green-500/20 rounded-xl p-4">
                <ShieldCheck className="w-5 h-5 text-green-400 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-green-400">Email verified</p>
                  <p className="text-xs text-gray-500 mt-0.5">{currentUser.email}</p>
                </div>
              </div>
            ) : (
              <div className="bg-yellow-500/5 border border-yellow-500/20 rounded-xl p-4">
                <div className="flex items-start gap-2.5">
                  <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-yellow-300">Email not verified</p>
                    <p className="text-xs text-gray-500 mt-0.5 mb-3">
                      Please verify your email address ({currentUser?.email}) to join campaigns. Check your inbox after clicking the button below.
                    </p>
                    <button
                      type="button"
                      disabled={sendingVerification || verificationSent}
                      onClick={async () => {
                        setSendingVerification(true);
                        try {
                          await sendEmailVerification(currentUser);
                          setVerificationSent(true);
                        } catch (err) {
                          console.error("Failed to send verification email:", err);
                          setError("Failed to send verification email. Please try again later.");
                        }
                        setSendingVerification(false);
                      }}
                      className="flex items-center gap-2 bg-yellow-500/20 hover:bg-yellow-500/30 disabled:opacity-50 text-yellow-300 px-4 py-2 rounded-lg transition-all text-xs font-medium"
                    >
                      {sendingVerification ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : verificationSent ? (
                        <CheckCircle2 className="w-3.5 h-3.5" />
                      ) : (
                        <Mail className="w-3.5 h-3.5" />
                      )}
                      {verificationSent ? "Verification email sent!" : "Send Verification Email"}
                    </button>
                    {verificationSent && (
                      <p className="text-xs text-gray-500 mt-2">
                        Didn't receive it? Check your spam folder, then refresh this page after verifying.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Username */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
              <Users className="w-4 h-4 text-gray-500" />
              Username
            </label>
            <p className="text-xs text-gray-600 mb-3">
              The name you're known by across your platforms
            </p>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value.replace(/\s/g, ""))}
              placeholder="e.g. NinjaGamer, xQc"
              className="w-full bg-gray-900/50 border border-gray-800 rounded-xl py-3 px-4 text-white placeholder-gray-600 focus:outline-none focus:border-brand-500/50 focus:ring-1 focus:ring-brand-500/50 transition-all"
            />
          </div>

          {/* Audience Size */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
              <Users className="w-4 h-4 text-gray-500" />
              Audience Size
            </label>
            <p className="text-xs text-gray-600 mb-3">
              Your total follower count across all platforms
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {AUDIENCE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setAudienceSize(opt.value)}
                  className={`text-sm py-2.5 px-3 rounded-xl border transition-all ${
                    audienceSize === opt.value
                      ? "bg-brand-600/20 border-brand-500/50 text-brand-400"
                      : "bg-gray-900/50 border-gray-800 text-gray-400 hover:border-gray-700"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Platforms */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
              <MonitorPlay className="w-4 h-4 text-gray-500" />
              Your Platforms
            </label>
            <p className="text-xs text-gray-600 mb-3">
              Select the platforms where you create content
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {PLATFORM_OPTIONS.map((platform) => {
                const isSelected = selectedPlatforms.includes(platform.id);
                return (
                  <button
                    key={platform.id}
                    type="button"
                    onClick={() => togglePlatform(platform.id)}
                    className={`relative flex items-center gap-2 px-4 py-3 rounded-xl border text-sm font-medium transition-all ${
                      isSelected
                        ? "bg-brand-600/20 border-brand-500/50 text-white"
                        : "bg-gray-900/50 border-gray-800 text-gray-400 hover:border-gray-700"
                    }`}
                  >
                    {platform.label}
                    {isSelected && (
                      <Check className="w-4 h-4 text-brand-400 absolute right-3" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Platform Links */}
          {selectedPlatforms.length > 0 && (
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
                <LinkIcon className="w-4 h-4 text-gray-500" />
                Platform Links
              </label>
              <p className="text-xs text-gray-600 mb-3">
                Add your profile or channel links
              </p>
              <div className="space-y-3">
                {selectedPlatforms.map((platformId) => {
                  const platform = PLATFORM_OPTIONS.find(
                    (p) => p.id === platformId
                  );
                  return (
                    <div key={platformId}>
                      <label className="block text-xs text-gray-500 mb-1">
                        {platform?.label}
                      </label>
                      <input
                        type="url"
                        value={links[platformId] || ""}
                        onChange={(e) =>
                          handleLinkChange(platformId, e.target.value)
                        }
                        placeholder={`https://${platformId === "twitter" ? "x" : platformId}.com/yourhandle`}
                        className="w-full bg-gray-900/50 border border-gray-800 rounded-xl py-2.5 px-4 text-white placeholder-gray-600 focus:outline-none focus:border-brand-500/50 focus:ring-1 focus:ring-brand-500/50 transition-all text-sm"
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Phone Number */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
              <Phone className="w-4 h-4 text-gray-500" />
              Phone Number
            </label>
            <p className="text-xs text-gray-600 mb-3">
              Your contact number for campaign communication
            </p>
            <input
              type="tel"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder="e.g. +1 555 123 4567"
              className="w-full bg-gray-900/50 border border-gray-800 rounded-xl py-3 px-4 text-white placeholder-gray-600 focus:outline-none focus:border-brand-500/50 focus:ring-1 focus:ring-brand-500/50 transition-all text-sm"
            />
          </div>

          {/* Payment Information */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
              <CreditCard className="w-4 h-4 text-gray-500" />
              Payment Information
            </label>
            <p className="text-xs text-gray-600 mb-3">
              How you'd like to receive payments
            </p>
            <div className="flex gap-3 mb-4">
              <button
                type="button"
                onClick={() => setPaymentMethod("paypal")}
                className={`flex-1 py-3 rounded-xl border text-sm font-medium transition-all ${
                  paymentMethod === "paypal"
                    ? "bg-blue-500/10 border-blue-500/50 text-blue-400"
                    : "bg-gray-900/50 border-gray-800 text-gray-400 hover:border-gray-700"
                }`}
              >
                PayPal
              </button>
              <button
                type="button"
                onClick={() => setPaymentMethod("wise")}
                className={`flex-1 py-3 rounded-xl border text-sm font-medium transition-all ${
                  paymentMethod === "wise"
                    ? "bg-green-500/10 border-green-500/50 text-green-400"
                    : "bg-gray-900/50 border-gray-800 text-gray-400 hover:border-gray-700"
                }`}
              >
                Wise
              </button>
              <button
                type="button"
                onClick={() => setPaymentMethod("iban")}
                className={`flex-1 py-3 rounded-xl border text-sm font-medium transition-all ${
                  paymentMethod === "iban"
                    ? "bg-orange-500/10 border-orange-500/50 text-orange-400"
                    : "bg-gray-900/50 border-gray-800 text-gray-400 hover:border-gray-700"
                }`}
              >
                IBAN
              </button>
            </div>
            {paymentMethod === "iban" ? (
              <div className="space-y-3">
                <input
                  type="text"
                  value={paymentName}
                  onChange={(e) => setPaymentName(e.target.value)}
                  placeholder="Account holder full name"
                  className="w-full bg-gray-900/50 border border-gray-800 rounded-xl py-3 px-4 text-white placeholder-gray-600 focus:outline-none focus:border-brand-500/50 focus:ring-1 focus:ring-brand-500/50 transition-all text-sm"
                />
                <input
                  type="text"
                  value={paymentIban}
                  onChange={(e) => setPaymentIban(e.target.value.toUpperCase())}
                  placeholder="e.g. TR00 0000 0000 0000 0000 0000 00"
                  className="w-full bg-gray-900/50 border border-gray-800 rounded-xl py-3 px-4 text-white placeholder-gray-600 focus:outline-none focus:border-brand-500/50 focus:ring-1 focus:ring-brand-500/50 transition-all text-sm font-mono"
                />
              </div>
            ) : (
              <input
                type="email"
                value={paymentEmail}
                onChange={(e) => setPaymentEmail(e.target.value)}
                placeholder={
                  paymentMethod === "paypal"
                    ? "your@paypal-email.com"
                    : "your@wise-email.com"
                }
                className="w-full bg-gray-900/50 border border-gray-800 rounded-xl py-3 px-4 text-white placeholder-gray-600 focus:outline-none focus:border-brand-500/50 focus:ring-1 focus:ring-brand-500/50 transition-all text-sm"
              />
            )}
          </div>

          {/* Error / Success */}
          {error && (
            <div className="flex items-center gap-2 text-sm text-red-400 bg-red-500/5 border border-red-500/20 rounded-xl p-3">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}
          {success && (
            <div className="flex items-center gap-2 text-sm text-green-400 bg-green-500/5 border border-green-500/20 rounded-xl p-3">
              <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
              Profile saved successfully!
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 bg-brand-600 hover:bg-brand-500 disabled:opacity-50 text-white py-3 rounded-xl transition-all font-semibold text-sm"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <CheckCircle2 className="w-4 h-4" />
            )}
            Save Profile
          </button>
        </form>
      </div>
    </div>
  );
}

export default InfluencerProfilePage;
